'use client';

import { useLayoutEffect } from 'react';

import { AnalyticEvents } from '@ps/analytics';
import { config } from '@ps/config';

import {
  captureAnalyticsSessionParams,
  getAnalyticsSessionCampaignParams,
} from './utils';

interface AnalyticsProviderProps {
  children: React.ReactNode;
}

export function AnalyticsProvider({ children }: AnalyticsProviderProps) {
  // Use useLayoutEffect to register listener BEFORE child useEffects run
  useLayoutEffect(() => {
    captureAnalyticsSessionParams();

    const pending: Array<[string, unknown, unknown]> = [];
    let flushTimer: ReturnType<typeof setInterval> | undefined;
    const flush = () => {
      if (!window.analytics?.track) return;
      clearInterval(flushTimer);
      flushTimer = undefined;
      for (const args of pending.splice(0)) {
        window.analytics.track(
          ...(args as Parameters<typeof window.analytics.track>),
        );
      }
    };

    // Register listener to forward events to Segment
    const removeListener = AnalyticEvents.default.addEventListener(
      (event, properties) => {
        // Log to console in development
        if (config.env === 'dev') {
          console.log('[Analytics] Event:', event, properties);
        }

        const options = {
          campaign: getAnalyticsSessionCampaignParams({ stripePrefix: true }),
        };
        // Segment loads lazyOnload, so events fired before it exists are queued, not dropped.
        if (window.analytics?.track) {
          window.analytics.track(event, properties, options);
        } else {
          pending.push([event, properties, options]);
          flushTimer ??= setInterval(flush, 500);
        }
      },
    );

    return () => {
      removeListener();
      if (flushTimer) clearInterval(flushTimer);
    };
  }, []);

  return <>{children}</>;
}
